import React, { useEffect, useMemo, useState } from 'react';
import { Ban, CalendarPlus, Eye, Search, X } from 'lucide-react';
import supabase from '../supabase/supabase';
import { TableSkeleton } from '../components/common/Skeleton';
import { track } from '../utils/analytics';

const money = (cents) => (Number(cents || 0) / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const date = (value) => value ? new Date(value).toLocaleDateString('pt-BR') : '—';
const lifecycleLabel = { paid: 'Pago', granted: 'Concedido', trial: 'Trial ativo', expired: 'Expirado' };
const lifecycleClass = {
  paid: 'bg-selo text-white',
  granted: 'bg-navy text-white',
  trial: 'bg-blue-50 text-blue-700 border border-blue-200',
  expired: 'bg-danger-tint text-danger border border-danger/20',
};
const sourceLabel = (provider) => provider === 'admin' ? 'Concedido pelo admin' : provider ? 'Pagamento' : 'Sem cobrança ativa';

const AdminBackoffice = () => {
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState('all');
  const [updatingId, setUpdatingId] = useState(null);

  const authFetch = async (url, options = {}) => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) throw new Error('Sessão expirada. Entre novamente.');
    return fetch(url, { ...options, headers: { ...(options.headers || {}), Authorization: `Bearer ${session.access_token}` } });
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await authFetch('/.netlify/functions/admin-backoffice-data');
      const result = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(result.error || 'Não foi possível carregar o backoffice.');
      setUsers(result.users || []);
      setStats(result.stats || {});
      track('admin_backoffice_loaded', {
        total_users: Number(result.stats?.totalUsers || 0),
        paid_active: Number(result.stats?.paidActive || 0),
        expired: Number(result.stats?.expired || 0),
        revenue_source: result.stats?.revenueSource || 'unknown',
      });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const postAdminAction = async (url, body) => {
    const response = await authFetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
    const result = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(result.error || 'Não foi possível concluir a alteração.');
    return result;
  };

  const changePlan = async (targetUserId, tier) => {
    try {
      setUpdatingId(targetUserId);
      await postAdminAction('/.netlify/functions/admin-set-plan', { userId: targetUserId, tier });
      track('admin_plan_changed', { target_tier: tier });
      await fetchData();
      setSelectedUser((current) => current?.id === targetUserId ? null : current);
    } catch (err) {
      setError(err.message);
    } finally {
      setUpdatingId(null);
    }
  };

  const extendTrial = async (targetUserId, days) => {
    try {
      setUpdatingId(targetUserId);
      await postAdminAction('/.netlify/functions/admin-extend-trial', { userId: targetUserId, days });
      track('admin_trial_extended', { extension_days: days });
      await fetchData();
      setSelectedUser(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setUpdatingId(null);
    }
  };

  const toggleBan = async (targetUser) => {
    const willBan = !targetUser.banned_at;
    let reason = '';
    if (willBan) {
      const input = window.prompt('Motivo do banimento (opcional):', targetUser.ban_reason || '');
      if (input === null) return;
      reason = input.trim();
      if (!window.confirm(`Banir ${targetUser.name || targetUser.email}? O acesso à conta será bloqueado imediatamente.`)) return;
    } else if (!window.confirm(`Reativar ${targetUser.name || targetUser.email}?`)) {
      return;
    }

    try {
      setUpdatingId(targetUser.id);
      await postAdminAction('/.netlify/functions/admin-ban-user', { userId: targetUser.id, banned: willBan, reason });
      track(willBan ? 'admin_user_banned' : 'admin_user_unbanned');
      await fetchData();
      setSelectedUser(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setUpdatingId(null);
    }
  };

  const filteredUsers = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return users.filter((user) => {
      const matchesText = !needle || [user.name, user.email, user.crm, user.plan_tier].some((value) => String(value || '').toLowerCase().includes(needle));
      const matchesFilter = filter === 'all'
        || (filter === 'banned' ? Boolean(user.banned_at) : (!user.banned_at && user.lifecycle?.accessStatus === filter));
      return matchesText && matchesFilter;
    });
  }, [users, query, filter]);

  const tierBadgeClass = (tier) => tier === 'pro' ? 'bg-navy text-white' : tier === 'rotina' ? 'bg-selo text-white' : 'bg-paper-alt text-navy border border-line';

  if (loading) return <div className="p-8"><TableSkeleton /></div>;

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      {selectedUser && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-navy/50 backdrop-blur-[2px]">
          <div className="bg-paper rounded-card w-full max-w-2xl shadow-xl overflow-hidden border border-line max-h-[92vh] overflow-y-auto">
            <div className="px-6 py-5 border-b border-line flex items-center justify-between bg-paper-alt/50">
              <div><h3 className="font-display font-bold text-navy">{selectedUser.name || 'Usuário'}</h3><p className="text-xs text-muted mt-1">{selectedUser.email}</p></div>
              <button onClick={() => setSelectedUser(null)} className="p-2.5 min-h-[44px] min-w-[44px] text-muted hover:text-ink rounded-card" aria-label="Fechar"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-6 grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
              <div className="p-4 bg-paper-alt rounded-card border border-line"><p className="text-[10px] text-muted uppercase tracking-widest">Conta</p><p className={`font-semibold mt-1 ${selectedUser.banned_at ? 'text-danger' : 'text-selo'}`}>{selectedUser.banned_at ? 'Banida' : 'Ativa'}</p></div>
              <div className="p-4 bg-paper-alt rounded-card border border-line"><p className="text-[10px] text-muted uppercase tracking-widest">Lifecycle</p><p className="font-semibold text-ink mt-1">{lifecycleLabel[selectedUser.lifecycle?.accessStatus] || '—'}</p></div>
              <div className="p-4 bg-paper-alt rounded-card border border-line"><p className="text-[10px] text-muted uppercase tracking-widest">Plano</p><p className="font-semibold text-ink mt-1 uppercase">{selectedUser.plan_tier || 'rotina'}</p></div>
              <div className="p-4 bg-paper-alt rounded-card border border-line"><p className="text-[10px] text-muted uppercase tracking-widest">Expira em</p><p className="font-semibold text-ink mt-1">{date(selectedUser.lifecycle?.expiresAt)}</p></div>
              <div className="p-4 bg-paper-alt rounded-card border border-line"><p className="text-[10px] text-muted uppercase tracking-widest">LTV</p><p className="font-semibold text-ink mt-1">{money(selectedUser.ltv_cents)}</p></div>
              <div className="p-4 bg-paper-alt rounded-card border border-line"><p className="text-[10px] text-muted uppercase tracking-widest">Laudos</p><p className="font-semibold text-ink mt-1">{selectedUser.report_count || 0}</p></div>
              <div className="p-4 bg-paper-alt rounded-card border border-line"><p className="text-[10px] text-muted uppercase tracking-widest">Origem</p><p className="font-semibold text-ink mt-1">{sourceLabel(selectedUser.subscription?.provider)}</p></div>
              <div className="p-4 bg-paper-alt rounded-card border border-line"><p className="text-[10px] text-muted uppercase tracking-widest">CRM/CRMV</p><p className="font-semibold text-ink mt-1">{selectedUser.crm || '—'}</p></div>
              <div className="p-4 bg-paper-alt rounded-card border border-line md:col-span-2"><p className="text-[10px] text-muted uppercase tracking-widest">Especialidade</p><p className="font-semibold text-ink mt-1">{selectedUser.specialty || '—'}</p></div>
            </div>
            {selectedUser.banned_at && selectedUser.ban_reason && <div className="mx-6 mb-4 bg-danger-tint border border-danger/20 text-danger text-xs rounded-card px-4 py-3">Motivo: {selectedUser.ban_reason}</div>}
            <div className="px-6 pb-6 space-y-4">
              <div><p className="text-[10px] text-muted uppercase tracking-widest mb-2">Conceder plano</p><div className="grid grid-cols-3 gap-2">{['inicial', 'rotina', 'pro'].map((tier) => <button key={tier} onClick={() => changePlan(selectedUser.id, tier)} disabled={updatingId === selectedUser.id || Boolean(selectedUser.banned_at)} className="btn-secondary text-xs uppercase">{tier}</button>)}</div></div>
              {selectedUser.lifecycle?.accessStatus !== 'paid' && !selectedUser.banned_at && <div><p className="text-[10px] text-muted uppercase tracking-widest mb-2">Estender trial Rotina</p><div className="grid grid-cols-3 gap-2">{[7, 14, 30].map((days) => <button key={days} onClick={() => extendTrial(selectedUser.id, days)} disabled={updatingId === selectedUser.id} className="btn-secondary text-xs"><CalendarPlus className="w-4 h-4" /> +{days} dias</button>)}</div></div>}
              <div className="pt-4 border-t border-line">
                <button onClick={() => toggleBan(selectedUser)} disabled={updatingId === selectedUser.id} className={selectedUser.banned_at ? 'btn-secondary w-full text-xs uppercase' : 'w-full min-h-[44px] rounded-card bg-danger text-white text-xs font-semibold uppercase tracking-widest hover:bg-danger/90 disabled:opacity-50 inline-flex items-center justify-center gap-2'}>
                  <Ban className="w-4 h-4" /> {selectedUser.banned_at ? 'Reativar usuário' : 'Banir usuário'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-7">
        <h1 className="font-display text-3xl font-bold text-navy tracking-tight">Backoffice</h1>
        <div className="grid grid-cols-2 gap-2">
          <div className="bg-navy text-white px-5 py-4 rounded-card min-w-[170px]"><p className="text-[10px] uppercase tracking-[0.18em] opacity-60">MRR</p><p className="text-xl font-bold tabular-nums mt-1">{money(stats.mrrCents)}</p></div>
          <div className="bg-selo text-white px-5 py-4 rounded-card min-w-[170px]"><p className="text-[10px] uppercase tracking-[0.18em] opacity-70">LTV somado</p><p className="text-xl font-bold tabular-nums mt-1">{money(stats.ltvCents)}</p></div>
        </div>
      </div>

      {error && <div className="mb-6 bg-danger-tint border border-danger/25 text-danger text-sm rounded-card px-4 py-3">{error}</div>}

      <div className="grid grid-cols-2 lg:grid-cols-6 gap-3 mb-6">
        <div className="bg-paper p-5 rounded-card border border-line"><p className="text-[10px] uppercase tracking-widest text-muted">Usuários reais</p><p className="text-2xl font-bold text-ink mt-1">{stats.totalUsers || 0}</p></div>
        <div className="bg-paper p-5 rounded-card border border-line"><p className="text-[10px] uppercase tracking-widest text-muted">Pagantes</p><p className="text-2xl font-bold text-ink mt-1">{stats.paidActive || 0}</p></div>
        <div className="bg-paper p-5 rounded-card border border-line"><p className="text-[10px] uppercase tracking-widest text-muted">Trials</p><p className="text-2xl font-bold text-ink mt-1">{stats.trialActive || 0}</p></div>
        <div className="bg-paper p-5 rounded-card border border-line"><p className="text-[10px] uppercase tracking-widest text-muted">Concedidos</p><p className="text-2xl font-bold text-ink mt-1">{stats.adminGranted || 0}</p></div>
        <div className="bg-paper p-5 rounded-card border border-line"><p className="text-[10px] uppercase tracking-widest text-muted">Expirados</p><p className="text-2xl font-bold text-ink mt-1">{stats.expired || 0}</p></div>
        <div className="bg-paper p-5 rounded-card border border-line"><p className="text-[10px] uppercase tracking-widest text-muted">Laudos</p><p className="text-2xl font-bold text-ink mt-1">{stats.totalReports || 0}</p></div>
      </div>

      <div className="bg-paper rounded-card border border-line shadow-card overflow-hidden">
        <div className="p-4 border-b border-line flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
          <div className="relative flex-1 max-w-md"><Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted" /><input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Nome, e-mail, CRM ou plano" className="input-field pl-10" /></div>
          <select value={filter} onChange={(e) => setFilter(e.target.value)} className="input-field sm:w-auto text-sm"><option value="all">Todos</option><option value="paid">Pagantes</option><option value="trial">Trial ativo</option><option value="granted">Concedidos</option><option value="expired">Expirados</option><option value="banned">Banidos</option></select>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead className="bg-paper-alt/80 text-[10px] font-semibold text-muted uppercase tracking-widest"><tr><th className="px-5 py-4">Usuário</th><th className="px-5 py-4">Status</th><th className="px-5 py-4">Plano</th><th className="px-5 py-4">Expiração</th><th className="px-5 py-4">Laudos</th><th className="px-5 py-4">LTV</th><th className="px-5 py-4 text-right">Ação</th></tr></thead>
            <tbody className="divide-y divide-line">
              {filteredUsers.map((user) => (
                <tr key={user.id} className="hover:bg-paper-alt/40 transition-colors">
                  <td className="px-5 py-4"><p className="font-semibold text-ink text-sm">{user.name || '—'}</p><p className="text-xs text-muted">{user.email}</p></td>
                  <td className="px-5 py-4">{user.banned_at ? <span className="px-2.5 py-1 rounded-full text-[10px] font-semibold uppercase tracking-wide bg-danger text-white">Banido</span> : <><span className={`px-2.5 py-1 rounded-full text-[10px] font-semibold uppercase tracking-wide ${lifecycleClass[user.lifecycle?.accessStatus] || lifecycleClass.expired}`}>{lifecycleLabel[user.lifecycle?.accessStatus] || 'Expirado'}</span>{user.lifecycle?.accessStatus === 'trial' && <p className="text-[10px] text-muted mt-1">{user.lifecycle?.trialDaysRemaining || 0} dias restantes</p>}</>}</td>
                  <td className="px-5 py-4"><span className={`px-2.5 py-1 rounded-full text-[10px] font-semibold uppercase tracking-wide ${tierBadgeClass(user.plan_tier)}`}>{user.plan_tier || 'rotina'}</span></td>
                  <td className="px-5 py-4 text-xs text-muted tabular-nums">{date(user.lifecycle?.expiresAt)}</td>
                  <td className="px-5 py-4 text-sm font-semibold text-ink tabular-nums">{user.report_count || 0}</td>
                  <td className="px-5 py-4 text-sm font-semibold text-ink tabular-nums">{money(user.ltv_cents)}</td>
                  <td className="px-5 py-4 text-right"><button onClick={() => { setSelectedUser(user); track('admin_user_opened', { access_status: user.banned_at ? 'banned' : user.lifecycle?.accessStatus || 'expired', plan_tier: user.plan_tier || 'rotina' }); }} className="p-2.5 min-h-[44px] min-w-[44px] text-muted hover:text-navy rounded-card" title="Gerenciar usuário" aria-label="Gerenciar usuário"><Eye className="w-4 h-4" /></button></td>
                </tr>
              ))}
              {!filteredUsers.length && <tr><td colSpan="7" className="px-6 py-10 text-center text-sm text-muted">Nenhum usuário encontrado.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
      <p className="text-[10px] text-muted mt-3">Receita: {stats.revenueSource === 'payments_hub' ? 'fonte canônica de pagamentos' : 'fallback local temporário'}. LTV médio {money(stats.averageLtvCents)}</p>
    </div>
  );
};

export default AdminBackoffice;
